import type { CoopClue, CoopCrosswordBoard } from '../../core/types/payloads/coop.types';

export function cellKey(row: number, col: number): string {
  return `${row}:${col}`;
}

export function parseCellKey(key: string): { row: number; col: number } {
  const [row, col] = key.split(':').map(Number);
  return { row, col };
}

export interface CrosswordGridMeta {
  rows: number;
  cols: number;
  blocked: Set<string>;
  solution: Record<string, string>;
  cellNumbers: Record<string, number>;
  clueNumbers: Record<string, number>;
  cluesByCell: Record<string, string[]>;
  clueCells: Record<string, string[]>;
}

export function cellsForClue(clue: CoopClue): string[] {
  const answer = clue.answer.toUpperCase().replace(/[^A-ZÁÉÍÓÚÑ]/g, '');
  if (answer.length !== clue.length) {
    throw new Error(`Clue ${clue.id}: answer length mismatch`);
  }
  const keys: string[] = [];
  for (let i = 0; i < clue.length; i++) {
    const row = clue.direction === 'down' ? clue.row + i : clue.row;
    const col = clue.direction === 'across' ? clue.col + i : clue.col;
    keys.push(cellKey(row, col));
  }
  return keys;
}

export function buildCrosswordGrid(board: CoopCrosswordBoard): CrosswordGridMeta {
  const blocked = new Set<string>();
  const solution: Record<string, string> = {};
  const cluesByCell: Record<string, string[]> = {};
  const clueCells: Record<string, string[]> = {};

  for (let r = 0; r < board.rows; r++) {
    for (let c = 0; c < board.cols; c++) {
      blocked.add(cellKey(r, c));
    }
  }

  for (const clue of board.clues) {
    const answer = clue.answer.toUpperCase().replace(/[^A-ZÁÉÍÓÚÑ]/g, '');
    if (answer.length !== clue.length) {
      throw new Error(`Clue "${clue.id}": answer must have ${clue.length} letters`);
    }

    const keys = cellsForClue(clue);
    clueCells[clue.id] = keys;

    for (let i = 0; i < keys.length; i++) {
      const key = keys[i]!;
      const letter = answer[i]!;
      if (key in solution && solution[key] !== letter) {
        throw new Error(`Clue "${clue.id}" conflicts at ${key}`);
      }
      blocked.delete(key);
      solution[key] = letter;
      if (!cluesByCell[key]) cluesByCell[key] = [];
      if (!cluesByCell[key]!.includes(clue.id)) cluesByCell[key]!.push(clue.id);
    }
  }

  const cellNumbers: Record<string, number> = {};
  const clueNumbers: Record<string, number> = {};
  let nextNum = 1;

  const sorted = [...board.clues].sort(
    (a, b) => a.row - b.row || a.col - b.col || a.direction.localeCompare(b.direction),
  );

  for (const clue of sorted) {
    const start = cellKey(clue.row, clue.col);
    if (!cellNumbers[start]) {
      cellNumbers[start] = nextNum;
      nextNum += 1;
    }
    clueNumbers[clue.id] = cellNumbers[start]!;
  }

  return {
    rows: board.rows,
    cols: board.cols,
    blocked,
    solution,
    cellNumbers,
    clueNumbers,
    cluesByCell,
    clueCells,
  };
}

export function isClueComplete(
  clueId: string,
  meta: CrosswordGridMeta,
  filledCells: Record<string, string>,
): boolean {
  const keys = meta.clueCells[clueId];
  if (!keys) return false;
  return keys.every((key) => filledCells[key]?.toUpperCase() === meta.solution[key]);
}

export function isPuzzleComplete(
  board: CoopCrosswordBoard,
  meta: CrosswordGridMeta,
  filledCells: Record<string, string>,
): boolean {
  return board.clues.every((c) => isClueComplete(c.id, meta, filledCells));
}

export function findClueById(board: CoopCrosswordBoard, clueId: string): CoopClue | undefined {
  return board.clues.find((c) => c.id === clueId);
}

export function nextEmptyCellInClue(
  clueId: string,
  meta: CrosswordGridMeta,
  filledCells: Record<string, string>,
): string | null {
  for (const key of meta.clueCells[clueId] ?? []) {
    if (!filledCells[key]) return key;
  }
  return null;
}

export function validateCrosswordBoard(board: CoopCrosswordBoard): void {
  if (board.rows < 3 || board.cols < 3) throw new Error('Crossword must be at least 3×3');
  if (!board.clues.length) throw new Error('Crossword needs at least one clue');
  for (const clue of board.clues) {
    const keys = cellsForClue(clue);
    for (const key of keys) {
      const { row, col } = parseCellKey(key);
      if (row < 0 || row >= board.rows || col < 0 || col >= board.cols) {
        throw new Error(`Clue "${clue.id}" out of grid bounds at ${key}`);
      }
    }
  }
  buildCrosswordGrid(board);
}

export function resolveActiveClue(
  board: CoopCrosswordBoard,
  meta: CrosswordGridMeta,
  cellKeyStr: string,
  currentClueId?: string | null,
): string | null {
  const ids = meta.cluesByCell[cellKeyStr];
  if (!ids?.length) return null;
  if (currentClueId && ids.includes(currentClueId)) return currentClueId;
  const across = ids.find((id) => findClueById(board, id)?.direction === 'across');
  return across ?? ids[0]!;
}

export const DEMO_CROSSWORD: CoopCrosswordBoard = {
  kind: 'crossword',
  rows: 6,
  cols: 6,
  clues: [
    {
      id: 'a1',
      direction: 'across',
      row: 0,
      col: 0,
      length: 4,
      clue: 'Hogar de aprendizaje',
      answer: 'CASA',
    },
    {
      id: 'd1',
      direction: 'down',
      row: 0,
      col: 0,
      length: 4,
      clue: 'Instituto escolar (abbr.)',
      answer: 'COLE',
    },
    {
      id: 'a2',
      direction: 'across',
      row: 2,
      col: 0,
      length: 5,
      clue: 'Obra literaria larga',
      answer: 'LIBRO',
    },
  ],
};
